<template>
  <IntercomModal
    v-model:show="showModal"
    :title="isEditMode ? 'Edit Quotation' : 'Create Quotation'"
    width="900px"
  >
    <div class="quotation-form" @keydown.escape="handleEscape">
      <!-- Customer & Customer Reference -->
      <div class="form-row">
        <div class="form-field" data-autofocus>
          <IntercomSelect
            ref="customerSelectRef"
            v-model="formData.customer_id"
            label="Customer"
            :options="customerSelectOptions"
            placeholder="Select Customer"
            filterable
            :disabled="isLoadingCustomers"
            @update:model-value="handleCustomerSelectChange"
            @keydown.enter="onSelectEnter('customer', $event)"
            @search="handleCustomerSearch"
          >
            <template #option="{ option }">
              <span :class="{ 'add-new-option': option.isCreateNew }">{{ option.label }}</span>
            </template>
          </IntercomSelect>

          <!-- Inline New Customer Form -->
          <div v-if="showNewCustomerForm" class="new-customer-form">
            <div class="new-customer-header">
              <span class="new-customer-title">New Customer</span>
            </div>
            <div class="new-customer-fields">
              <IntercomInput
                v-model="newCustomer.name"
                label="Name"
                placeholder="Customer name"
                :disabled="isCreatingCustomer"
              />
              <IntercomPhoneInput
                v-model="newCustomer.phone"
                label="Phone"
                :disabled="isCreatingCustomer"
                @update:valid="(v) => (newCustomerPhoneValid = v)"
              />
              <IntercomInput
                v-model="newCustomer.email"
                label="Email"
                placeholder="Optional"
                :disabled="isCreatingCustomer"
              />
            </div>
            <div class="new-customer-actions">
              <button type="button" class="cancel-inline-btn" @click="cancelNewCustomer">
                Cancel
              </button>
              <IntercomButton
                variant="primary"
                size="sm"
                :loading="isCreatingCustomer"
                :disabled="!newCustomer.name || (newCustomer.phone && !newCustomerPhoneValid)"
                @click="createAndSelectCustomer"
              >
                Create & Select
              </IntercomButton>
            </div>
          </div>
        </div>

        <div class="form-field">
          <IntercomInput
            ref="customerRefRef"
            v-model="formData.customer_reference"
            label="Customer Reference / PO Number"
            placeholder="Optional reference"
            @keydown.enter.prevent="advance('customerRef')"
          />
        </div>
      </div>

      <!-- Quotation Date & Valid Until -->
      <div class="form-row">
        <div class="form-field">
          <IntercomDateInput
            v-model="formData.quotation_date"
            label="Quotation Date"
          />
        </div>

        <div class="form-field">
          <IntercomDateInput
            v-model="formData.valid_until"
            label="Valid Until"
            :min="formData.quotation_date || undefined"
            @keydown.enter.prevent="advance('validUntil')"
          />
          <span class="form-hint">Leave blank for no expiry</span>
        </div>
      </div>

      <!-- Notes -->
      <div class="form-row">
        <div class="form-field" style="grid-column: 1 / -1;">
          <IntercomInput
            v-model="formData.notes"
            label="Notes"
            placeholder="Optional notes for the customer"
            type="textarea"
            :rows="2"
          />
        </div>
      </div>

      <!-- Terms and Conditions -->
      <div class="form-row">
        <div class="form-field" style="grid-column: 1 / -1;">
          <IntercomInput
            v-model="formData.terms_and_conditions"
            label="Terms and Conditions"
            placeholder="Payment terms, delivery terms, etc."
            type="textarea"
            :rows="2"
          />
        </div>
      </div>

      <!-- Line Items Section -->
      <div class="section-divider">
        <span class="section-title">Line Items</span>
      </div>

      <!-- Line Items Table -->
      <div class="line-items-table">
        <div class="line-items-header">
          <div class="col-product">Product</div>
          <div class="col-hsn">HSN</div>
          <div class="col-qty">Qty</div>
          <div class="col-price">Rate</div>
          <div class="col-disc">Disc</div>
          <div class="col-gst">GST%</div>
          <div class="col-total">Amount</div>
          <div class="col-actions"></div>
        </div>

        <div
          v-for="(item, index) in formData.items"
          :key="index"
          class="line-item-row"
        >
          <div class="col-product">
            <IntercomSelect
              :ref="(el) => setItemRef(index, 'product', el)"
              v-model="item.product_id"
              :options="productOptions"
              placeholder="Type to search products..."
              filterable
              @update:model-value="(v) => handleProductSelect(index, v)"
              @search="handleProductSearch"
              @keydown.enter="(e) => { if (!e.defaultPrevented) { e.preventDefault(); handleItemEnter(index, 'product') } }"
            />
          </div>

          <div class="col-hsn">
            <span class="readonly-display">{{ item.hsn_code || '-' }}</span>
          </div>

          <div class="col-qty">
            <input
              :ref="(el) => setItemRef(index, 'qty', el)"
              v-model.number="item.quantity"
              type="number"
              min="0.01"
              step="0.01"
              class="item-input"
              placeholder="Qty"
              @keydown.enter.prevent="handleItemEnter(index, 'qty')"
              @keydown.left="handleItemArrowLeft(index, 'qty', $event)"
              @keydown.right="handleItemArrowRight(index, 'qty', $event)"
            />
          </div>

          <div class="col-price">
            <input
              :ref="(el) => setItemRef(index, 'price', el)"
              v-model.number="item.unit_price"
              type="number"
              min="0"
              step="0.01"
              class="item-input"
              placeholder="Rate"
              @keydown.enter.prevent="handleItemEnter(index, 'price')"
              @keydown.left="handleItemArrowLeft(index, 'price', $event)"
              @keydown.right="handleItemArrowRight(index, 'price', $event)"
            />
          </div>

          <div class="col-disc">
            <div class="disc-input-wrap">
              <input
                :ref="(el) => setItemRef(index, 'discount', el)"
                v-model.number="item.discount_value"
                type="number"
                min="0"
                step="0.01"
                class="item-input disc-input"
                placeholder="0"
                @keydown.enter.prevent="handleItemEnter(index, 'discount')"
                @keydown.left="handleItemArrowLeft(index, 'discount', $event)"
                @keydown.right="handleItemArrowRight(index, 'discount', $event)"
              />
              <button
                type="button"
                class="disc-toggle"
                :title="item.discount_type === 'percentage' ? 'Percentage' : 'Flat amount'"
                @click="toggleDiscountType(index)"
              >{{ item.discount_type === 'percentage' ? '%' : '&#8377;' }}</button>
            </div>
          </div>

          <div class="col-gst">
            <span class="readonly-display">{{ item.is_taxable ? (item.igst_rate > 0 ? item.igst_rate : item.cgst_rate + item.sgst_rate) + '%' : '-' }}</span>
          </div>

          <div class="col-total">
            <span class="total-display">&#8377;{{ calculateItemTotal(item).toFixed(2) }}</span>
          </div>

          <div class="col-actions">
            <button
              type="button"
              class="remove-btn"
              :disabled="formData.items.length === 1"
              @click="removeLineItem(index)"
            >
              &#10005;
            </button>
          </div>
        </div>

        <button type="button" class="add-item-btn" @click="addLineItem">
          + Add Item
        </button>
      </div>

      <!-- Quotation-Level Discount -->
      <div class="quotation-discount-section">
        <span class="quotation-discount-label">Quotation Discount:</span>
        <div class="quotation-discount-input">
          <input
            ref="quotationDiscountRef"
            v-model.number="formData.discount_value"
            type="number"
            min="0"
            step="0.01"
            class="item-input"
            placeholder="0"
            @keydown.enter.prevent="advance('quotationDiscount')"
          />
          <button
            type="button"
            class="disc-toggle"
            @click="toggleQuotationDiscountType"
          >{{ formData.discount_type === 'percentage' ? '%' : '&#8377;' }}</button>
        </div>
      </div>

      <!-- Totals Section -->
      <div class="totals-section">
        <div class="totals-row">
          <span class="totals-label">Subtotal:</span>
          <span class="totals-value">&#8377;{{ quotationTotals.subtotal.toFixed(2) }}</span>
        </div>
        <div v-if="quotationTotals.itemDiscount > 0" class="totals-row discount-row">
          <span class="totals-label">Item Discounts:</span>
          <span class="totals-value">-&#8377;{{ quotationTotals.itemDiscount.toFixed(2) }}</span>
        </div>
        <div v-if="quotationTotals.quotationDiscount > 0" class="totals-row discount-row">
          <span class="totals-label">Quotation Discount:</span>
          <span class="totals-value">-&#8377;{{ quotationTotals.quotationDiscount.toFixed(2) }}</span>
        </div>
        <div class="totals-row">
          <span class="totals-label">Taxable Amount:</span>
          <span class="totals-value">&#8377;{{ quotationTotals.taxable.toFixed(2) }}</span>
        </div>
        <div v-if="quotationTotals.cgst > 0" class="totals-row">
          <span class="totals-label">CGST:</span>
          <span class="totals-value cgst">&#8377;{{ quotationTotals.cgst.toFixed(2) }}</span>
        </div>
        <div v-if="quotationTotals.sgst > 0" class="totals-row">
          <span class="totals-label">SGST:</span>
          <span class="totals-value sgst">&#8377;{{ quotationTotals.sgst.toFixed(2) }}</span>
        </div>
        <div v-if="quotationTotals.igst > 0" class="totals-row">
          <span class="totals-label">IGST:</span>
          <span class="totals-value igst">&#8377;{{ quotationTotals.igst.toFixed(2) }}</span>
        </div>
        <div class="totals-row grand-total">
          <span class="totals-label">Grand Total:</span>
          <span class="totals-value">&#8377;{{ quotationTotals.total.toFixed(2) }}</span>
        </div>
      </div>
    </div>

    <template #footer>
      <IntercomButton variant="secondary" @click="handleCancel">
        Cancel
      </IntercomButton>
      <IntercomButton
        variant="primary"
        :loading="isSaving"
        :disabled="!canSubmit"
        @click="handleSubmit"
      >
        {{ isEditMode ? 'Update Quotation' : 'Save Quotation' }}
      </IntercomButton>
    </template>
  </IntercomModal>
</template>

<script setup>
import { ref, reactive, watch, computed, nextTick } from 'vue'
import { useToast } from '@shared/composables/useToast.js'
import { useFormKeyboard } from '@shared/composables/useFormKeyboard.js'
import { IntercomModal, IntercomButton, IntercomInput, IntercomDateInput, IntercomPhoneInput, IntercomSelect } from '@shared/components/intercom'
import { useBilling } from '../composables/useBilling.js'

const props = defineProps({
  show: Boolean,
  quotation: { type: Object, default: null },
})

const emit = defineEmits(['update:show', 'success'])

const toast = useToast()
const { listCustomers, createCustomer, listProducts, createQuotation, updateQuotation } = useBilling()

// Edit mode
const isEditMode = computed(() => !!props.quotation)

// State
const showModal = ref(props.show)
const isLoadingCustomers = ref(false)
const isLoadingProducts = ref(false)
const isSaving = ref(false)
const customerOptions = ref([])
const productOptions = ref([])
const allProducts = ref([])

// --- Keyboard navigation ---
const customerSelectRef = ref(null)
const validUntilRef = ref(null)
const customerRefRef = ref(null)
const quotationDiscountRef = ref(null)

// Refs for line item fields: itemRefs[index] = { product, qty, price, discount }
const itemRefs = ref({})
const setItemRef = (index, field, el) => {
  if (!itemRefs.value[index]) itemRefs.value[index] = {}
  itemRefs.value[index][field] = el
}

const { field: kbField, advance, moveLeft, moveRight, handleEscape, clearFields, focusFirst } = useFormKeyboard({
  onSubmit: () => { if (canSubmit.value) handleSubmit() },
  onEscape: () => { showModal.value = false },
})

// Re-register fields when items change
const registerKeyboardFields = () => {
  clearFields()
  kbField('customer', customerSelectRef)
  kbField('validUntil', validUntilRef)
  kbField('customerRef', customerRefRef)
  // notes and terms are textareas — skip them in the Enter chain
  formData.items.forEach((_, i) => {
    const refs = itemRefs.value[i] || {}
    kbField(`item-${i}-product`, ref(refs.product), { type: 'select' })
    kbField(`item-${i}-qty`, ref(refs.qty), { type: 'number', row: `item-${i}` })
    kbField(`item-${i}-price`, ref(refs.price), { type: 'number', row: `item-${i}` })
    kbField(`item-${i}-discount`, ref(refs.discount), { type: 'number', row: `item-${i}` })
  })
  kbField('quotationDiscount', quotationDiscountRef, { type: 'number' })
}

// Handle Enter on IntercomSelect: advance when dropdown is closed (event not prevented)
const onSelectEnter = (fieldName, e) => {
  if (!e.defaultPrevented) {
    e.preventDefault()
    advance(fieldName)
  }
}

// Helper for line item Enter/Arrow handlers
const handleItemEnter = (index, field) => advance(`item-${index}-${field}`)
const handleItemArrowLeft = (index, field, e) => moveLeft(`item-${index}-${field}`, e)
const handleItemArrowRight = (index, field, e) => moveRight(`item-${index}-${field}`, e)

// Customer select options with "+ New Customer" sentinel at end
const customerSelectOptions = computed(() => {
  return [
    ...customerOptions.value,
    { value: '__create_new__', label: '+ New Customer', isCreateNew: true },
  ]
})

// Inline new customer form
const showNewCustomerForm = ref(false)
const isCreatingCustomer = ref(false)
const newCustomerPhoneValid = ref(false)
const newCustomer = reactive({ name: '', phone: '', email: '' })

// Default item template
const defaultItem = () => ({
  product_id: null,
  quantity: 1,
  unit_price: 0,
  hsn_code: null,
  is_taxable: false,
  cgst_rate: 0,
  sgst_rate: 0,
  igst_rate: 0,
  discount_type: 'percentage',
  discount_value: 0,
})

// Today's date and default valid_until (15 days from today)
const todayISO = new Date().toISOString().slice(0, 10)
const defaultValidUntil = (() => {
  const d = new Date()
  d.setDate(d.getDate() + 15)
  return d.toISOString().slice(0, 10)
})()

// Form data
const formData = reactive({
  customer_id: null,
  customer_reference: '',
  quotation_date: todayISO,
  valid_until: defaultValidUntil,
  notes: '',
  terms_and_conditions: '',
  discount_type: 'fixed',
  discount_value: 0,
  items: [defaultItem()],
})

// Computed: Can submit form
const canSubmit = computed(() => {
  if (formData.items.length === 0) return false
  return formData.items.every((item) => {
    return item.product_id && item.quantity > 0 && item.unit_price > 0
  })
})

// Computed: Quotation totals — GST-exclusive (base prices, tax added on top)
// Same calculation as PurchaseBillForm (tax-exclusive model)
const quotationTotals = computed(() => {
  let subtotal = 0
  let totalItemDiscount = 0
  let cgst = 0
  let sgst = 0
  let igst = 0

  const itemTaxables = formData.items.map((item) => {
    const lineBase = (item.quantity || 0) * (item.unit_price || 0)
    let discount = 0
    if (item.discount_value > 0) {
      if (item.discount_type === 'percentage') {
        discount = lineBase * (Math.min(item.discount_value, 100) / 100)
      } else {
        discount = Math.min(item.discount_value, lineBase)
      }
    }
    totalItemDiscount += discount
    const taxableAmount = lineBase - discount
    subtotal += lineBase
    return { taxableAmount, item }
  })

  const netSubtotal = subtotal - totalItemDiscount

  // Quotation-level discount
  let quotationDisc = 0
  if (formData.discount_value > 0) {
    if (formData.discount_type === 'percentage') {
      quotationDisc = netSubtotal * (Math.min(formData.discount_value, 100) / 100)
    } else {
      quotationDisc = Math.min(formData.discount_value, netSubtotal)
    }
  }

  const taxableTotal = netSubtotal - quotationDisc

  // Compute tax proportionally per item
  itemTaxables.forEach(({ taxableAmount, item }) => {
    if (!item.is_taxable || netSubtotal === 0) return
    const proportion = taxableAmount / netSubtotal
    const adjustedTaxable = taxableAmount - (quotationDisc * proportion)

    if (item.cgst_rate > 0) cgst += adjustedTaxable * item.cgst_rate / 100
    if (item.sgst_rate > 0) sgst += adjustedTaxable * item.sgst_rate / 100
    if (item.igst_rate > 0) igst += adjustedTaxable * item.igst_rate / 100
  })

  const totalTax = cgst + sgst + igst
  return {
    subtotal,
    itemDiscount: totalItemDiscount,
    quotationDiscount: quotationDisc,
    taxable: taxableTotal,
    cgst,
    sgst,
    igst,
    total: taxableTotal + totalTax,
  }
})

// Watch show prop
watch(
  () => props.show,
  async (val) => {
    showModal.value = val
    if (val) {
      fetchCustomers()
      await fetchProducts()
      if (props.quotation) {
        populateFromQuotation(props.quotation)
      } else {
        resetForm()
      }
    }
  }
)

// Watch modal state — focus first field when modal opens
watch(showModal, (val) => {
  emit('update:show', val)
  if (val) {
    nextTick(() => focusFirst())
  }
})

// Re-register keyboard fields when line items change
watch(
  () => formData.items.length,
  () => nextTick(registerKeyboardFields),
  { immediate: true }
)

/**
 * Fetch customers for dropdown
 */
const fetchCustomers = async () => {
  isLoadingCustomers.value = true
  try {
    const response = await listCustomers({ limit: 100 })
    customerOptions.value = (response.data || []).map((c) => ({
      label: c.name,
      value: c.id,
    }))
  } catch (error) {
    console.error('Failed to fetch customers:', error)
    customerOptions.value = []
  } finally {
    isLoadingCustomers.value = false
  }
}

/**
 * Fetch products for dropdown
 */
const fetchProducts = async () => {
  isLoadingProducts.value = true
  try {
    const response = await listProducts({ limit: 100 })
    allProducts.value = response.data || []
    productOptions.value = allProducts.value.map((p) => ({
      label: `${p.name} - \u20B9${p.base_price}`,
      value: p.id,
      product: p,
    }))
  } catch (error) {
    console.error('Failed to fetch products:', error)
    productOptions.value = []
  } finally {
    isLoadingProducts.value = false
  }
}

/**
 * Server-side customer search. Debounced. Empty/short query restores the
 * default top-100 list. Keeps the selected customer visible even if absent
 * from search results.
 */
let customerSearchTimer = null
const handleCustomerSearch = (query) => {
  if (customerSearchTimer) clearTimeout(customerSearchTimer)
  if (!query || query.length < 2) {
    fetchCustomers()
    return
  }
  customerSearchTimer = setTimeout(async () => {
    try {
      const response = await listCustomers({ search: query, limit: 20 })
      const results = response.data || []
      const toOpt = (c) => ({ label: c.name, value: c.id })
      const selectedId = formData.customer_id
      const resultIds = new Set(results.map((r) => r.id))
      const selectedOption = selectedId && !resultIds.has(selectedId)
        ? customerOptions.value.find((o) => o.value === selectedId)
        : null
      customerOptions.value = [
        ...(selectedOption ? [selectedOption] : []),
        ...results.map(toOpt),
      ]
    } catch (error) {
      console.error('Customer search failed:', error)
    }
  }, 250)
}

/**
 * Server-side product search
 */
let productSearchTimer = null
const handleProductSearch = (query) => {
  if (productSearchTimer) clearTimeout(productSearchTimer)
  if (!query || query.length < 2) {
    productOptions.value = allProducts.value.map((p) => ({
      label: `${p.name} - \u20B9${p.base_price}`,
      value: p.id,
      product: p,
    }))
    return
  }
  productSearchTimer = setTimeout(async () => {
    try {
      const response = await listProducts({ search: query, limit: 20 })
      const results = response.data || []
      const existingIds = new Set(allProducts.value.map(p => p.id))
      for (const r of results) {
        if (!existingIds.has(r.id)) {
          allProducts.value.push(r)
        }
      }
      const toOpt = (p) => ({
        label: `${p.name} - \u20B9${p.base_price}`,
        value: p.id,
        product: p,
      })
      const selectedIds = new Set(
        formData.items.map(i => i.product_id).filter(Boolean)
      )
      const resultIds = new Set(results.map(r => r.id))
      const selectedOptions = allProducts.value
        .filter(p => selectedIds.has(p.id) && !resultIds.has(p.id))
        .map(toOpt)
      productOptions.value = [...selectedOptions, ...results.map(toOpt)]
    } catch (error) {
      console.error('Product search failed:', error)
    }
  }, 250)
}

/**
 * Handle customer select change — intercept the sentinel value
 */
const handleCustomerSelectChange = (value) => {
  if (value === '__create_new__') {
    nextTick(() => {
      formData.customer_id = null
    })
    showNewCustomerForm.value = true
  }
}

/**
 * Cancel inline customer creation
 */
const cancelNewCustomer = () => {
  showNewCustomerForm.value = false
  Object.assign(newCustomer, { name: '', phone: '', email: '' })
  newCustomerPhoneValid.value = false
}

/**
 * Create new customer inline and auto-select them
 */
const createAndSelectCustomer = async () => {
  if (!newCustomer.name.trim()) {
    toast.warning('Customer name is required')
    return
  }
  isCreatingCustomer.value = true
  try {
    let phone = newCustomer.phone || null
    if (phone && phone.startsWith('+91')) phone = phone.slice(3)

    const created = await createCustomer({
      name: newCustomer.name.trim(),
      phone: phone || null,
      email: newCustomer.email || null,
    })
    const customer = created?.customer || created

    customerOptions.value.unshift({ label: customer.name, value: customer.id })
    formData.customer_id = customer.id

    showNewCustomerForm.value = false
    Object.assign(newCustomer, { name: '', phone: '', email: '' })
    newCustomerPhoneValid.value = false

    toast.success('Customer created')
  } catch (error) {
    console.error('Failed to create customer:', error)
    toast.error('Failed to create customer')
  } finally {
    isCreatingCustomer.value = false
  }
}

/**
 * Handle product selection — auto-fill unit_price and tax rates
 */
const handleProductSelect = (index, productId) => {
  const item = formData.items[index]
  const pid = productId ?? item.product_id
  if (!pid) return

  const product = allProducts.value.find((p) => p.id === pid)
  if (!product) return

  item.unit_price = product.base_price
  item.hsn_code = product.hsn_code || null
  item.is_taxable = product.is_taxable || false
  item.cgst_rate = product.is_taxable ? (product.cgst_rate ?? 0) : 0
  item.sgst_rate = product.is_taxable ? (product.sgst_rate ?? 0) : 0
  item.igst_rate = 0
}

/**
 * Calculate item total (after item-level discount)
 */
const calculateItemTotal = (item) => {
  const lineBase = (item.quantity || 0) * (item.unit_price || 0)
  let discount = 0
  if (item.discount_value > 0) {
    if (item.discount_type === 'percentage') {
      discount = lineBase * (Math.min(item.discount_value, 100) / 100)
    } else {
      discount = Math.min(item.discount_value, lineBase)
    }
  }
  return lineBase - discount
}

/**
 * Toggle item-level discount type
 */
const toggleDiscountType = (index) => {
  const item = formData.items[index]
  item.discount_type = item.discount_type === 'percentage' ? 'fixed' : 'percentage'
  item.discount_value = 0
}

/**
 * Toggle quotation-level discount type
 */
const toggleQuotationDiscountType = () => {
  formData.discount_type = formData.discount_type === 'percentage' ? 'fixed' : 'percentage'
  formData.discount_value = 0
}

/**
 * Add new line item
 */
const addLineItem = () => {
  formData.items.push(defaultItem())
}

/**
 * Remove line item
 */
const removeLineItem = (index) => {
  if (formData.items.length > 1) {
    formData.items.splice(index, 1)
  }
}

/**
 * Populate form from existing quotation for editing
 */
const populateFromQuotation = (q) => {
  showNewCustomerForm.value = false
  Object.assign(newCustomer, { name: '', phone: '', email: '' })
  formData.customer_id = q.customer_id || null
  formData.customer_reference = q.customer_reference || ''
  formData.quotation_date = q.quotation_date ? new Date(q.quotation_date).toISOString().slice(0, 10) : todayISO
  formData.valid_until = q.valid_until ? new Date(q.valid_until).toISOString().slice(0, 10) : ''
  formData.notes = q.notes || ''
  formData.terms_and_conditions = q.terms_and_conditions || ''
  formData.discount_type = q.discount_type || 'fixed'
  formData.discount_value = q.discount_value || 0
  formData.items = (q.items || []).map((item) => ({
    product_id: item.product_id,
    quantity: parseFloat(Number(item.quantity).toFixed(3)),
    unit_price: item.unit_price,
    hsn_code: item.hsn_code || null,
    is_taxable: !!(item.cgst_rate || item.sgst_rate || item.igst_rate),
    cgst_rate: item.cgst_rate || 0,
    sgst_rate: item.sgst_rate || 0,
    igst_rate: item.igst_rate || 0,
    discount_type: item.discount_type || 'percentage',
    discount_value: item.discount_value || 0,
  }))
  if (formData.items.length === 0) {
    formData.items = [defaultItem()]
  }

  // Inject quotation's products into options if they weren't in the paginated fetch
  for (const item of (q.items || [])) {
    if (!item.product_id) continue
    const exists = allProducts.value.some((p) => p.id === item.product_id)
    if (!exists) {
      const product = {
        id: item.product_id,
        name: item.product_name || 'Unknown Product',
        base_price: item.unit_price,
        hsn_code: item.hsn_code,
        is_taxable: !!(item.cgst_rate || item.sgst_rate || item.igst_rate),
        cgst_rate: item.cgst_rate || 0,
        sgst_rate: item.sgst_rate || 0,
      }
      allProducts.value.push(product)
      productOptions.value.push({
        label: `${product.name} - \u20B9${product.base_price}`,
        value: product.id,
        product,
      })
    }
  }
}

/**
 * Reset form
 */
const resetForm = () => {
  showNewCustomerForm.value = false
  Object.assign(newCustomer, { name: '', phone: '', email: '' })
  formData.customer_id = null
  formData.customer_reference = ''
  formData.quotation_date = todayISO
  formData.valid_until = defaultValidUntil
  formData.notes = ''
  formData.terms_and_conditions = ''
  formData.discount_type = 'fixed'
  formData.discount_value = 0
  formData.items = [defaultItem()]
}

/**
 * Handle cancel
 */
const handleCancel = () => {
  showModal.value = false
  resetForm()
}

/**
 * Handle submit
 */
const handleSubmit = async () => {
  if (!canSubmit.value) return

  isSaving.value = true
  try {
    const data = {
      customer_id: formData.customer_id || null,
      customer_reference: formData.customer_reference || null,
      quotation_date: formData.quotation_date || null,
      valid_until: formData.valid_until || null,
      notes: formData.notes || null,
      terms_and_conditions: formData.terms_and_conditions || null,
      discount_type: formData.discount_value > 0 ? formData.discount_type : null,
      discount_value: formData.discount_value || 0,
      items: formData.items.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        cgst_rate: item.is_taxable ? item.cgst_rate : 0,
        sgst_rate: item.is_taxable ? item.sgst_rate : 0,
        igst_rate: item.is_taxable ? item.igst_rate : 0,
        discount_type: item.discount_value > 0 ? item.discount_type : null,
        discount_value: item.discount_value || 0,
      })),
    }

    if (isEditMode.value) {
      await updateQuotation(props.quotation.id, data)
      toast.success('Quotation updated')
    } else {
      await createQuotation(data)
      toast.success('Quotation created')
    }

    showModal.value = false
    resetForm()
    emit('success')
  } catch (error) {
    const action = isEditMode.value ? 'update' : 'create'
    console.error(`Failed to ${action} quotation:`, error)
    toast.error(`Failed to ${action} quotation: ` + (error.message || 'Unknown error'))
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped>
.quotation-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.form-input {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-3);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  outline: none;
  transition: all var(--transition-fast);
}

.form-input:focus {
  background: var(--color-white);
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

.form-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}

.section-divider {
  position: relative;
  text-align: center;
  margin: var(--space-2) 0;
}

.section-divider::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: var(--color-gray-200);
}

.section-title {
  position: relative;
  display: inline-block;
  padding: 0 var(--space-3);
  background: var(--color-white);
  color: var(--color-gray-600);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.line-items-table {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.line-items-header,
.line-item-row {
  display: grid;
  grid-template-columns: 1fr 80px 70px 90px 90px 55px 90px 36px;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-2) var(--space-3);
}

.line-items-header {
  background: var(--color-gray-100);
  border-radius: var(--radius-md);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-600);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.line-item-row {
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-gray-200);
}

.col-product {
  min-width: 0;
  overflow: hidden;
}

.col-product :deep(.n-base-selection-label) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-hsn,
.col-gst {
  text-align: center;
}

.col-total {
  text-align: right;
}

.item-input {
  width: 100%;
  height: 36px;
  padding: 0 var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  outline: none;
  transition: all var(--transition-fast);
}

.item-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(0, 87, 255, 0.1);
}

.item-input[type='number'] {
  text-align: right;
}

/* Discount input with toggle */
.disc-input-wrap {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-white);
}

.disc-input {
  border: none !important;
  border-radius: 0 !important;
  height: 32px !important;
  width: 50px;
  min-width: 0;
}

.disc-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  background: var(--color-gray-100);
  border: none;
  border-left: 1px solid var(--color-gray-200);
  cursor: pointer;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-600);
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.disc-toggle:hover {
  background: var(--color-gray-200);
  color: var(--color-charcoal);
}

/* Quotation-level discount */
.quotation-discount-section {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-3);
  margin-bottom: var(--space-3);
}

.quotation-discount-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.quotation-discount-input {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-white);
  width: 140px;
}

.quotation-discount-input .item-input {
  border: none !important;
  height: 36px;
}

.quotation-discount-input .disc-toggle {
  height: 36px;
}

/* Discount row in totals */
.totals-row.discount-row .totals-value {
  color: var(--color-green-600, #16a34a);
}

.total-display {
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.readonly-display {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.totals-value.cgst { color: #2563eb; }
.totals-value.sgst { color: #7c3aed; }
.totals-value.igst { color: #0891b2; }

.remove-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--color-gray-500);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-size: 16px;
}

.remove-btn:hover:not(:disabled) {
  background: #fee;
  color: #d03050;
}

.remove-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.add-item-btn {
  align-self: flex-start;
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  background: transparent;
  border: 1px dashed var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.add-item-btn:hover {
  background: rgba(0, 87, 255, 0.05);
  border-color: var(--color-blue);
}

.totals-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  margin-left: auto;
  min-width: 280px;
  max-width: 320px;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-2) 0;
}

.totals-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  font-weight: var(--font-weight-medium);
}

.totals-value {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-semibold);
}

.totals-row.grand-total {
  border-top: 2px solid var(--color-gray-300);
  padding-top: var(--space-3);
  margin-top: var(--space-2);
}

.totals-row.grand-total .totals-label {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-weight: var(--font-weight-bold);
}

.totals-row.grand-total .totals-value {
  font-size: var(--font-size-lg);
  color: var(--color-blue);
  font-weight: var(--font-weight-bold);
}

/* Sentinel "+ New Customer" option */
.add-new-option {
  color: var(--color-blue);
  font-weight: var(--font-weight-medium);
}

.new-customer-form {
  margin-top: var(--space-3);
  padding: var(--space-4);
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: var(--radius-lg);
}

.new-customer-header {
  margin-bottom: var(--space-3);
}

.new-customer-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #1d4ed8;
}

.new-customer-fields {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
}

.new-customer-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding-top: var(--space-3);
  border-top: 1px solid #bfdbfe;
}

.cancel-inline-btn {
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: transparent;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.cancel-inline-btn:hover {
  background: var(--color-gray-50);
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .new-customer-fields {
    grid-template-columns: 1fr;
  }
}
</style>
